import React from "react";
import { useTheme } from "../contexts/ThemeContext";
import SEOHead from "./SEOHead";

const UniversityResponse = () => {
    const { colors } = useTheme();

    return (
        <>
        <SEOHead
            title="Our Response — Collegia Data Practices Clarification"
            description="Read our official response and clarification about Collegia's data practices. Transparency about how we handle student data with our privacy-first approach."
            keywords="Collegia data practices, Collegia response, student data privacy, Collegia transparency, university app data"
            url="https://collegia.app/university_response"
        />
        <div className={`${colors.bg} min-h-screen py-8 px-4`}>
            <div className="max-w-4xl mx-auto">
                {/* Header Section */}
                <div className="text-center mb-12">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-blue-500 to-green-600 rounded-full mb-6">
                        <span className="text-2xl">🤝</span>
                    </div>
                    <h1 className={`text-3xl md:text-5xl font-bold mb-4 ${colors.text} bg-gradient-to-r from-blue-400 to-green-600 bg-clip-text text-transparent leading-tight`}>
                        Clarification on Data Practices
                    </h1>
                    <p className={`text-xl ${colors.textSecondary} font-light mb-2`}>
                        Addressing Concerns About Our Student-Built Application
                    </p>
                    <p className={`${colors.textMuted} text-lg`}>
                        A transparent explanation of our privacy-first, local-processing approach
                    </p>
                </div>

                {/* Opening Statement */}
                <div className={`${colors.card} rounded-2xl p-8 border ${colors.border} backdrop-blur-sm mb-8 bg-gradient-to-r from-blue-500/5 to-green-500/5`}>
                    <div className="flex items-start space-x-4">
                        <div className="flex-shrink-0 w-8 h-8 bg-blue-500/20 rounded-full flex items-center justify-center">
                            <span className="text-blue-500">📋</span>
                        </div>
                        <div>
                            <h2 className={`text-2xl font-semibold mb-4 ${colors.text}`}>
                                Our Commitment to Transparency
                            </h2>
                            <p className={`${colors.textSecondary} leading-relaxed text-lg`}>
                                As dedicated students who have invested years in creating this application, we want to 
                                provide complete clarity about our data practices and technical architecture. This app 
                                was created to <span className={`${colors.text} font-semibold`}>improve the student experience</span> 
                                through better interface design and functionality.
                            </p>
                        </div>
                    </div>
                </div>

                {/* Technical Architecture */}
                <div className={`${colors.card} rounded-2xl p-8 border ${colors.border} backdrop-blur-sm mb-8`}>
                    <div className="flex items-start space-x-4">
                        <div className="flex-shrink-0 w-8 h-8 bg-green-500/20 rounded-full flex items-center justify-center">
                            <span className="text-green-500">🏗️</span>
                        </div>
                        <div>
                            <h2 className={`text-2xl font-semibold mb-4 ${colors.text}`}>
                                How Our Application Actually Works
                            </h2>
                            <div className={`${colors.textSecondary} leading-relaxed text-lg space-y-4`}>
                                <p>
                                    Our application functions as a <span className={`${colors.text} font-semibold`}>presentation interface only</span>. 
                                    Here's exactly what happens when students use our app:
                                </p>
                                <div className="grid md:grid-cols-1 gap-4 mt-6 ml-4">
                                    <div className="flex items-start space-x-3">
                                        <span className="w-6 h-6 bg-green-500 text-white rounded-full flex items-center justify-center text-sm font-bold mt-1">1</span>
                                        <div>
                                            <strong className={colors.text}>Local Authentication:</strong> Students enter their own credentials directly on their device
                                        </div>
                                    </div>
                                    <div className="flex items-start space-x-3">
                                        <span className="w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-sm font-bold mt-1">2</span>
                                        <div>
                                            <strong className={colors.text}>Direct University Connection:</strong> The app connects directly to the university's official system using those credentials
                                        </div>
                                    </div>
                                    <div className="flex items-start space-x-3">
                                        <span className="w-6 h-6 bg-purple-500 text-white rounded-full flex items-center justify-center text-sm font-bold mt-1">3</span>
                                        <div>
                                            <strong className={colors.text}>Local Processing:</strong> All data processing and display happens entirely on the student's device
                                        </div>
                                    </div>
                                    <div className="flex items-start space-x-3">
                                        <span className="w-6 h-6 bg-orange-500 text-white rounded-full flex items-center justify-center text-sm font-bold mt-1">4</span>
                                        <div>
                                            <strong className={colors.text}>No Data Transmission:</strong> No university data is sent to our servers or stored anywhere except locally
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Data Practices Clarification */}
                <div className={`${colors.card} rounded-2xl p-8 border ${colors.border} backdrop-blur-sm mb-8 border-green-500/30 bg-green-500/5`}>
                    <div className="flex items-start space-x-4">
                        <div className="flex-shrink-0 w-8 h-8 bg-green-500/20 rounded-full flex items-center justify-center">
                            <span className="text-green-500">🔒</span>
                        </div>
                        <div>
                            <h2 className={`text-2xl font-semibold mb-4 ${colors.text}`}>
                                What We DO NOT Do
                            </h2>
                            <div className={`${colors.textSecondary} leading-relaxed text-lg space-y-4`}>
                                <p>
                                    To address any concerns about data practices, here's what we explicitly <span className={`${colors.text} font-semibold`}>do not do</span>:
                                </p>
                                <div className="grid md:grid-cols-2 gap-4 mt-6">
                                    <div className="flex items-start space-x-3">
                                        <span className="text-red-500 text-xl">❌</span>
                                        <span><strong>Store university data</strong> on our servers</span>
                                    </div>
                                    <div className="flex items-start space-x-3">
                                        <span className="text-red-500 text-xl">❌</span>
                                        <span><strong>Scrape or cache</strong> any academic information</span>
                                    </div>
                                    <div className="flex items-start space-x-3">
                                        <span className="text-red-500 text-xl">❌</span>
                                        <span><strong>Collect or retain</strong> student credentials</span>
                                    </div>
                                    <div className="flex items-start space-x-3">
                                        <span className="text-red-500 text-xl">❌</span>
                                        <span><strong>Transmit academic data</strong> to third parties</span>
                                    </div>
                                    <div className="flex items-start space-x-3">
                                        <span className="text-red-500 text-xl">❌</span>
                                        <span><strong>Create databases</strong> of university information</span>
                                    </div>
                                    <div className="flex items-start space-x-3">
                                        <span className="text-red-500 text-xl">❌</span>
                                        <span><strong>Perform unauthorized access</strong> to systems</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* What We Actually Do */}
                <div className={`${colors.card} rounded-2xl p-8 border ${colors.border} backdrop-blur-sm mb-8`}>
                    <div className="flex items-start space-x-4">
                        <div className="flex-shrink-0 w-8 h-8 bg-blue-500/20 rounded-full flex items-center justify-center">
                            <span className="text-blue-500">✨</span>
                        </div>
                        <div>
                            <h2 className={`text-2xl font-semibold mb-4 ${colors.text}`}>
                                What We Actually Provide
                            </h2>
                            <div className={`${colors.textSecondary} leading-relaxed text-lg space-y-4`}>
                                <p>
                                    Our application serves as an <span className={`${colors.text} font-semibold`}>interface improvement layer</span>:
                                </p>
                                <div className="grid md:grid-cols-2 gap-4 mt-6">
                                    <div className="flex items-start space-x-3">
                                        <span className="text-green-500 text-xl">✅</span>
                                        <span><strong>Better user interface</strong> for academic data</span>
                                    </div>
                                    <div className="flex items-start space-x-3">
                                        <span className="text-green-500 text-xl">✅</span>
                                        <span><strong>Improved accessibility</strong> and navigation</span>
                                    </div>
                                    <div className="flex items-start space-x-3">
                                        <span className="text-green-500 text-xl">✅</span>
                                        <span><strong>Enhanced mobile experience</strong> for students</span>
                                    </div>
                                    <div className="flex items-start space-x-3">
                                        <span className="text-green-500 text-xl">✅</span>
                                        <span><strong>Local data organization</strong> and presentation</span>
                                    </div>
                                    <div className="flex items-start space-x-3">
                                        <span className="text-green-500 text-xl">✅</span>
                                        <span><strong>Offline functionality</strong> when possible</span>
                                    </div>
                                    <div className="flex items-start space-x-3">
                                        <span className="text-green-500 text-xl">✅</span>
                                        <span><strong>Student-centric features</strong> and tools</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Legal Compliance */}
                <div className={`${colors.card} rounded-2xl p-8 border ${colors.border} backdrop-blur-sm mb-8`}>
                    <div className="flex items-start space-x-4">
                        <div className="flex-shrink-0 w-8 h-8 bg-purple-500/20 rounded-full flex items-center justify-center">
                            <span className="text-purple-500">⚖️</span>
                        </div>
                        <div>
                            <h2 className={`text-2xl font-semibold mb-4 ${colors.text}`}>
                                Our Legal Position
                            </h2>
                            <div className={`${colors.textSecondary} leading-relaxed text-lg space-y-4`}>
                                <p>
                                    We believe our application operates within <span className={`${colors.text} font-semibold`}>full legal compliance</span> because:
                                </p>
                                <ul className="space-y-3 mt-4 ml-6">
                                    <li className="flex items-start">
                                        <span className="text-purple-500 font-bold mt-1">•</span>
                                        <span className="ml-3">We function as an interface layer, similar to web browsers or mobile apps accessing websites</span>
                                    </li>
                                    <li className="flex items-start">
                                        <span className="text-purple-500 font-bold mt-1">•</span>
                                        <span className="ml-3">Students use their own legitimate credentials to access their own data</span>
                                    </li>
                                    <li className="flex items-start">
                                        <span className="text-purple-500 font-bold mt-1">•</span>
                                        <span className="ml-3">No unauthorized access or data collection occurs</span>
                                    </li>
                                    <li className="flex items-start">
                                        <span className="text-purple-500 font-bold mt-1">•</span>
                                        <span className="ml-3">We maintain transparent practices and open communication</span>
                                    </li>
                                    <li className="flex items-start">
                                        <span className="text-purple-500 font-bold mt-1">•</span>
                                        <span className="ml-3">Our app existed before the official university app, demonstrating independent innovation</span>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Development History */}
                <div className={`${colors.card} rounded-2xl p-8 border ${colors.border} backdrop-blur-sm mb-8`}>
                    <div className="flex items-start space-x-4">
                        <div className="flex-shrink-0 w-8 h-8 bg-orange-500/20 rounded-full flex items-center justify-center">
                            <span className="text-orange-500">🏆</span>
                        </div>
                        <div>
                            <h2 className={`text-2xl font-semibold mb-4 ${colors.text}`}>
                                Our Development Journey
                            </h2>
                            <div className={`${colors.textSecondary} leading-relaxed text-lg space-y-4`}>
                                <p>
                                    We developed this application <span className={`${colors.text} font-semibold`}>years before</span> any official 
                                    university mobile app existed, demonstrating:
                                </p>
                                <div className="grid md:grid-cols-2 gap-4 mt-6">
                                    <div className="flex items-start space-x-3">
                                        <span className="w-2 h-2 bg-orange-500 rounded-full mt-2 flex-shrink-0"></span>
                                        <span><strong>Independent innovation</strong> and student initiative</span>
                                    </div>
                                    <div className="flex items-start space-x-3">
                                        <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></span>
                                        <span><strong>Original development</strong> not copying existing solutions</span>
                                    </div>
                                    <div className="flex items-start space-x-3">
                                        <span className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></span>
                                        <span><strong>Years of refinement</strong> based on student feedback</span>
                                    </div>
                                    <div className="flex items-start space-x-3">
                                        <span className="w-2 h-2 bg-purple-500 rounded-full mt-2 flex-shrink-0"></span>
                                        <span><strong>Enhanced security measures</strong> implemented over time</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Industry Context */}
                <div className={`${colors.card} rounded-2xl p-8 border ${colors.border} backdrop-blur-sm mb-8 border-blue-500/30 bg-blue-500/5`}>
                    <div className="flex items-start space-x-4">
                        <div className="flex-shrink-0 w-8 h-8 bg-blue-500/20 rounded-full flex items-center justify-center">
                            <span className="text-blue-500">🌐</span>
                        </div>
                        <div>
                            <h2 className={`text-2xl font-semibold mb-4 ${colors.text}`}>
                                Industry Standard Practices
                            </h2>
                            <div className={`${colors.textSecondary} leading-relaxed text-lg space-y-4`}>
                                <p>
                                    Our approach aligns with standard industry practices where applications serve as 
                                    <span className={`${colors.text} font-semibold`}> interface layers</span> for existing systems:
                                </p>
                                <ul className="space-y-3 mt-4 ml-6">
                                    <li className="flex items-start">
                                        <span className="text-blue-500 font-bold mt-1">•</span>
                                        <span className="ml-3">Banking apps that provide better interfaces for online banking</span>
                                    </li>
                                    <li className="flex items-start">
                                        <span className="text-blue-500 font-bold mt-1">•</span>
                                        <span className="ml-3">Email clients that connect to various email providers</span>
                                    </li>
                                    <li className="flex items-start">
                                        <span className="text-blue-500 font-bold mt-1">•</span>
                                        <span className="ml-3">Social media management tools that access platform APIs</span>
                                    </li>
                                    <li className="flex items-start">
                                        <span className="text-blue-500 font-bold mt-1">•</span>
                                        <span className="ml-3">Academic platforms that integrate with university systems</span>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Our Commitment */}
                <div className={`${colors.card} rounded-2xl p-8 border ${colors.border} backdrop-blur-sm mb-8 bg-gradient-to-r from-green-500/5 to-blue-500/5`}>
                    <div className="flex items-start space-x-4">
                        <div className="flex-shrink-0 w-8 h-8 bg-green-500/20 rounded-full flex items-center justify-center">
                            <span className="text-green-500">🤝</span>
                        </div>
                        <div>
                            <h2 className={`text-2xl font-semibold mb-4 ${colors.text}`}>
                                Our Ongoing Commitment
                            </h2>
                            <div className={`${colors.textSecondary} leading-relaxed text-lg space-y-4`}>
                                <p>
                                    We remain committed to <span className={`${colors.text} font-semibold`}>serving the student community</span> 
                                    responsibly and transparently:
                                </p>
                                <div className="grid md:grid-cols-2 gap-4 mt-6">
                                    <div className="flex items-start space-x-3">
                                        <span className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></span>
                                        <span>Maintaining complete data transparency</span>
                                    </div>
                                    <div className="flex items-start space-x-3">
                                        <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></span>
                                        <span>Continuing privacy-first development</span>
                                    </div>
                                    <div className="flex items-start space-x-3">
                                        <span className="w-2 h-2 bg-purple-500 rounded-full mt-2 flex-shrink-0"></span>
                                        <span>Open communication with all stakeholders</span>
                                    </div>
                                    <div className="flex items-start space-x-3">
                                        <span className="w-2 h-2 bg-orange-500 rounded-full mt-2 flex-shrink-0"></span>
                                        <span>Willingness to address any specific concerns</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Closing Statement */}
                <div className={`${colors.card} rounded-2xl p-8 border ${colors.border} backdrop-blur-sm mb-8`}>
                    <div className="flex items-start space-x-4">
                        <div className="flex-shrink-0 w-8 h-8 bg-purple-500/20 rounded-full flex items-center justify-center">
                            <span className="text-purple-500">💜</span>
                        </div>
                        <div>
                            <h2 className={`text-2xl font-semibold mb-4 ${colors.text}`}>
                                In Conclusion
                            </h2>
                            <div className={`${colors.textSecondary} leading-relaxed text-lg space-y-4`}>
                                <p>
                                    We hope this clarifies our data practices and technical approach. Our application represents 
                                    <span className={`${colors.text} font-semibold`}> student innovation</span> in improving digital experiences 
                                    within established, authorized frameworks.
                                </p>
                                <p>
                                    We welcome any specific questions or concerns and remain committed to operating with complete 
                                    transparency and respect for all policies and stakeholders.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Contact Section */}
                <div className="text-center mt-12 pt-8 border-t border-gray-700/20">
                    <div className={`${colors.card} rounded-2xl p-6 border ${colors.border} backdrop-blur-sm inline-block`}>
                        <p className={`${colors.text} font-semibold text-lg mb-2`}>
                            With Transparency and Respect,
                        </p>
                        <p className={`${colors.textSecondary} text-lg`}>
                            The Student Development Team
                        </p>
                        <p className={`${colors.textMuted} text-sm mt-2`}>
                            Independent Student Developers
                        </p>
                    </div>
                    
                    <div className="flex justify-center space-x-4 mt-8">
                        <a 
                            href="mailto:contact@collegia.app"
                            className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-green-500 to-blue-500 text-white font-semibold rounded-lg hover:shadow-lg transition-all duration-300 transform hover:scale-105"
                        >
                            📧 Contact Us
                        </a>
                        <a 
                            href="/privacy_policy"
                            className={`inline-flex items-center px-6 py-3 ${colors.buttonSecondary} font-semibold rounded-lg hover:shadow-lg transition-all duration-300`}
                        >
                            🔒 Privacy Policy
                        </a>
                        <a 
                            href="/"
                            className={`inline-flex items-center px-6 py-3 ${colors.buttonSecondary} font-semibold rounded-lg hover:shadow-lg transition-all duration-300`}
                        >
                            🏠 Back to Home
                        </a>
                    </div>
                </div>
            </div>
        </div>
        </>
    );
};

export default UniversityResponse;
